/**
 * App Root Component
 *
 * Wraps the entire app in AuthProvider and GameProvider.
 * Conditionally renders AuthDialog or Board based on authentication state.
 */

import { useRef, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { GameProvider } from './contexts/GameContext';
import { useAuth } from './hooks/useAuth';
import Board from './components/Board';
import Header from './components/Header';
import LoginBoard from './components/LoginBoard';
import WelcomePanel from './components/WelcomePanel';
import { AuthDialog } from './components/auth';
import PuzzleEditor from './components/editor/PuzzleEditor';
import { parsePuzzle, parsePuzzleJSON } from './utils/puzzleParser';

interface SelectedPuzzle {
  id: string;
  name: string;
  puzzleNumber?: number | null;
  puzzleText: string;
  date: string;
  sampleIndex?: number; // 1-based index for sample games
  puzzleJson?: import('./types').PuzzleJSON | null; // prefer over puzzleText when available
}

/** Display name for a selected puzzle (used in WelcomePanel) */
function puzzleDisplayName(puzzle: SelectedPuzzle | null): string {
  if (!puzzle) return '';
  if (puzzle.sampleIndex !== undefined) return `SAMPLE PUZZLE ${puzzle.sampleIndex}`;
  return new Date(puzzle.date + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

function AppContent() {
  const { user, profile, isLoading, error: authError, signOut } = useAuth();
  const wasShowingLogin = useRef(false);
  // skipFlipTransition: true when user is already logged in on mount — suppress animation
  const skipFlipTransition = useRef(false);
  const [loginFlipped, setLoginFlipped] = useState(false);
  const [selectedPuzzle, setSelectedPuzzle] = useState<SelectedPuzzle | null>(null);
  // When true, the game board is mounted (GameProvider + Board)
  const [gameActive, setGameActive] = useState(false);
  // Controls whether Board auto-starts on mount. False when switching puzzles from sample grid.
  const [autoStartNext, setAutoStartNext] = useState(true);

  // Reset state when user logs out
  useEffect(() => {
    if (!user) {
      console.log('[App] user became null — resetting game state');
      setSelectedPuzzle(null);
      setGameActive(false);
      setLoginFlipped(false);
      skipFlipTransition.current = false;
    }
  }, [user]);

  // Logout handler — passed to Header. Fire-and-forget signOut;
  // state resets via onAuthStateChange SIGNED_OUT event.
  const handleLogout = useCallback(() => {
    console.log('[App] handleLogout called');
    signOut().catch(err => console.error('[App] Logout error:', err));
  }, [signOut]);

  // When transitioning from login → authenticated, trigger flip.
  // If the user was already authenticated on mount (page refresh), skip the flip animation.
  useLayoutEffect(() => {
    if (user && !isLoading) {
      if (wasShowingLogin.current) {
        // Came from login screen — flip immediately to hide the auth form
        wasShowingLogin.current = false;
        setLoginFlipped(true);
      } else if (!loginFlipped) {
        // Already authenticated on page load — show WelcomePanel immediately (no animation)
        skipFlipTransition.current = true;
        setLoginFlipped(true);
      }
    }
  }, [user, isLoading, loginFlipped]);

  // Handle Start/Resume/Review click from WelcomePanel (on LoginBoard back face).
  // No crossfade — WelcomePanel covers the paillette area, so the swap from
  // LoginBoard to Board is invisible. A fade would cause ghost doubling
  // (two WelcomePanels visible at once).
  const handleStartFromWelcome = useCallback(() => {
    if (selectedPuzzle) {
      setAutoStartNext(true); // auto-start when coming from LoginBoard
      setGameActive(true);
    }
  }, [selectedPuzzle]);

  // Handle going back to puzzle select from the game board
  const handleBackToMain = useCallback(() => {
    setGameActive(false);
    setSelectedPuzzle(null);
    skipFlipTransition.current = true; // no flip animation when returning to menu
    setLoginFlipped(true); // Keep showing the back face (WelcomePanel/SampleSelect)
  }, []);

  // Handle reset from WelcomePanel — clear selected puzzle's localStorage state
  const handleResetFromWelcome = useCallback(() => {
    if (selectedPuzzle) {
      try { localStorage.removeItem(`this-and-that-game-${selectedPuzzle.date}`); } catch {}
      // Force re-render to update CTA variant
      setSelectedPuzzle(prev => prev ? { ...prev } : null);
    }
  }, [selectedPuzzle]);

  // Show loading state — only for initial page load (not during sign-in transitions).
  // During sign-in, keep showing the auth dialog to avoid flashing.
  if (isLoading && !wasShowingLogin.current) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#1a1a1a',
        color: '#f7e5c5',
        fontFamily: 'Inter, sans-serif',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  // Track whether we were showing login for loading state
  if (!user || !profile) {
    wasShowingLogin.current = true;
  }

  // Render Board when game is active. During crossfade, both LoginBoard and Board
  // are mounted simultaneously — LoginBoard fades out while Board is visible behind it.
  const showBoard = gameActive && selectedPuzzle;
  const showLogin = !gameActive;

  return (
    <>
      {/* LoginBoard — single instance with flip card. Front=auth, Back=welcome/samples.
           Always rendered when not in-game. flipped=true when user is authenticated. */}
      {showLogin && (
        <>
          <Header onLogout={handleLogout} />
          <LoginBoard
            flipped={loginFlipped}
            skipTransition={skipFlipTransition.current}
            fadingOut={false}
            backChildren={
              <WelcomePanel
                selectedPuzzle={selectedPuzzle}
                puzzleDisplayName={puzzleDisplayName(selectedPuzzle)}
                onStart={handleStartFromWelcome}
                onSelectPuzzle={setSelectedPuzzle}
                onReset={handleResetFromWelcome}
                risePhase="idle"
                inline
              />
            }
          >
            <AuthDialog />
            {authError && (
              <div style={{
                position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
                background: '#b64624', color: '#f7e5c5', padding: '8px 16px',
                borderRadius: 6, fontSize: 13, fontFamily: 'Inter, sans-serif',
                maxWidth: 360, textAlign: 'center', zIndex: 100,
              }}>
                {authError}
              </div>
            )}
          </LoginBoard>
        </>
      )}

      {/* Board — mounts immediately when game starts, paints behind fading LoginBoard */}
      {showBoard && (
        <GameProvider
          key={selectedPuzzle.id}
          initialPuzzle={
            selectedPuzzle.puzzleJson
              ? (() => { const pd = parsePuzzleJSON(selectedPuzzle.puzzleJson, selectedPuzzle.date); pd.id = selectedPuzzle.id; pd.puzzleNumber = selectedPuzzle.puzzleNumber ?? undefined; return { puzzleData: pd }; })()
              : (() => { const pd = parsePuzzle(selectedPuzzle.puzzleText, selectedPuzzle.date); pd.id = selectedPuzzle.id; pd.puzzleNumber = selectedPuzzle.puzzleNumber ?? undefined; return { puzzleData: pd }; })()
          }
        >
          <Board
            onMain={handleBackToMain}
            onSelectPuzzle={(puzzle) => {
              setSelectedPuzzle(puzzle);
              setAutoStartNext(false); // don't auto-start — player should see the CTA first
              setGameActive(true);
            }}
            puzzleName={selectedPuzzle.name}
            sampleIndex={selectedPuzzle.sampleIndex}
            autoStart={autoStartNext}
            onLogout={handleLogout}
          />
        </GameProvider>
      )}
    </>
  );
}

/** Play-test mode: fetches a puzzle by ID and renders a fully playable game (no auth). */
function PlayTestMode({ puzzleId }: { puzzleId: string }) {
  const [puzzleInfo, setPuzzleInfo] = useState<{
    initialPuzzle: { puzzleData: import('./types').PuzzleData } | { puzzleText: string; date: string };
    name: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

    fetch(`${supabaseUrl}/rest/v1/puzzles_admin?id=eq.${puzzleId}&select=id,puzzle_number,puzzle_text,puzzle_json,date,name`, {
      headers: {
        'apikey': supabaseKey,
        'Authorization': `Bearer ${supabaseKey}`,
      },
    })
      .then(res => res.json())
      .then(rows => {
        if (!rows.length) { setError('Puzzle not found'); return; }
        const row = rows[0];
        const date = row.date ?? '2026-01-01';
        const name = row.puzzle_json?.solution?.primary ?? row.name ?? 'Test Puzzle';

        if (row.puzzle_json) {
          const puzzleData = parsePuzzleJSON(row.puzzle_json, date);
          puzzleData.id = row.id;
          puzzleData.puzzleNumber = row.puzzle_number;
          setPuzzleInfo({ initialPuzzle: { puzzleData }, name });
        } else if (row.puzzle_text) {
          const puzzleData = parsePuzzle(row.puzzle_text, date);
          puzzleData.id = row.id;
          puzzleData.puzzleNumber = row.puzzle_number;
          setPuzzleInfo({ initialPuzzle: { puzzleData }, name });
        } else {
          setError('Puzzle has no content');
        }
      })
      .catch(() => setError('Failed to load puzzle'));
  }, [puzzleId]);

  if (error) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#1a1a1a', color: '#f7e5c5',
        fontFamily: 'Inter, sans-serif', fontSize: '18px',
      }}>
        {error}
      </div>
    );
  }

  if (!puzzleInfo) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#1a1a1a', color: '#f7e5c5',
        fontFamily: 'Inter, sans-serif', fontSize: '18px',
      }}>
        Loading puzzle...
      </div>
    );
  }

  return (
    <AuthProvider>
      <GameProvider
        key={puzzleId}
        initialPuzzle={puzzleInfo.initialPuzzle}
      >
        <div style={{ paddingTop: 8, background: '#1a1a1a', minHeight: '100vh' }}>
          <Board
            onMain={() => window.close()}
            puzzleName={puzzleInfo.name}
          />
        </div>
      </GameProvider>
    </AuthProvider>
  );
}

function App() {
  const params = new URLSearchParams(window.location.search);
  const isEditor = params.get('editor') === 'true';
  const playId = params.get('play');

  if (isEditor) {
    return <PuzzleEditor />;
  }

  if (playId) {
    return <PlayTestMode puzzleId={playId} />;
  }

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
